import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  useSidebar,
} from '@bpinternal/ui-kit-next'
import { useRouter } from '@tanstack/react-router'
import { Flag, KeyRound, Link, LogOut, Sun, User as UserIcon } from 'lucide-react'
import { type FC, type ReactNode, useState } from 'react'
import { Avatar } from '~/elements'
import { useAppStore } from '~/stores'
import { copyToClipboard } from '~/utils'
import type { User } from '../../features/users/types'
import { config } from '../../shared'
import { showBugReportDialog } from '../BugReportDialog'
import { AppearanceDropdownItems } from './AppearanceDropdownItems'

type Props = {
  className?: string
  user: User
  children: ReactNode
  disabled?: boolean
  asChild?: boolean
}

export const UserDropdown: FC<Props> = ({ children, disabled, asChild, user }) => {
  const activeWorkspace = useAppStore((s) => s.activeWorkspace)
  const navigate = useRouter().navigate
  const [open, setOpen] = useState(false) // Need to controle the state for now because of a bug in radix, see: https://github.com/radix-ui/primitives/issues/837#issuecomment-2716644471
  const { isMobile, setOpenMobile } = useSidebar()

  const handleAccountSettingsNavigation = () => {
    if (!activeWorkspace?.id) {
      return
    }

    if (isMobile) {
      setOpenMobile(false)
    }

    void navigate({
      to: '/workspaces/$workspaceId/profile/account',
      params: { workspaceId: activeWorkspace?.id },
    })
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
      }}
    >
      <DropdownMenuTrigger asChild={asChild} disabled={disabled}>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-w-72 min-w-60" align="end">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Avatar
            name={user?.displayName || user?.email}
            pictureUrl={user?.profilePicture}
            className="h-8 w-8 rounded-full"
            variant="soft"
          />
          <div className="grid flex-1 text-left text-sm leading-tight font-normal">
            <span className="truncate font-semibold">{user?.displayName}</span>
            <span className="truncate text-xs">{user?.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleAccountSettingsNavigation}>
          <UserIcon />
          <span>Account Settings</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => location.assign(config.ssoBaseUrl.concat('/link'))}>
          <Link />
          <span>Link social accounts</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => location.assign(config.ssoBaseUrl.concat('/settings'))}>
          <KeyRound />
          <span>Change password</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => {
            setOpen(false)
            showBugReportDialog({
              onSubmit: ({ id }) =>
                void copyToClipboard(id, 'issue ID', {
                  event: e,
                  preventDefault: true,
                }),
            })
          }}
        >
          <Flag />
          <span>Report a bug</span>
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Sun />
            <span>Appearance</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <AppearanceDropdownItems />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => location.replace(config.ssoBaseUrl.concat('/logout'))}>
          <LogOut />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
